/**
 * NotificationProvider — React context for in-app notifications.
 *
 * Polls the real notifications table every 60 seconds. Provides
 * unread count, mark-as-read (persisted to DB), and refresh actions.
 */

import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuthStore } from '../state_management_and_stores/auth.store';
import {
  fetchNotifications,
  markAllNotificationsRead,
  type AppNotification,
} from '../services/notificationService';

// ─── Context Type ─────────────────────────────────────────────────────────────

export interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  markAllRead: () => void;
  refresh: () => void;
  isLoading: boolean;
}

export const NotificationContext =
  createContext<NotificationContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 60_000;

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { profile } = useAuthStore();
  const userProfileId = profile?.id ?? '';
  const isAuthenticated = !!userProfileId;

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const doFetch = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const result = await fetchNotifications(userProfileId);
      setNotifications(result);
    } catch (err) {
      console.warn('[NotificationProvider] Failed to fetch notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, userProfileId]);

  // Initial fetch + polling
  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      return;
    }
    doFetch();
    const interval = setInterval(doFetch, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, doFetch]);

  // Mark all as read — persisted to DB
  const markAllRead = useCallback(async () => {
    if (!userProfileId) return;
    // Optimistically mark local state
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await markAllNotificationsRead(userProfileId);
    } catch (err) {
      console.warn('[NotificationProvider] Failed to mark all read:', err);
      // Revert on failure
      doFetch();
    }
  }, [userProfileId, doFetch]);

  const refresh = useCallback(() => {
    doFetch();
  }, [doFetch]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      unreadCount,
      markAllRead,
      refresh,
      isLoading,
    }),
    [notifications, unreadCount, markAllRead, refresh, isLoading]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
